import React from 'react';
import { Link } from 'react-router-dom';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <Link to="/" className="text-zinc-500 hover:text-zinc-900 text-sm uppercase tracking-wider mb-8 inline-block">
            ← Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-light text-zinc-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-zinc-500 text-sm">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-zinc max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">1. Introduction</h2>
            <p className="text-zinc-600 leading-relaxed">
              Arbcor Systems ("Company," "we," "us," or "our") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform, software, and related services (collectively, the "Service").
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">2. Information We Collect</h2>
            <p className="text-zinc-600 leading-relaxed mb-4">
              We may collect the following types of information:
            </p>
            <h3 className="text-lg font-medium text-zinc-800 mb-2">Personal Information</h3>
            <ul className="list-disc list-inside text-zinc-600 mb-4 space-y-1">
              <li>Name and contact information (email address, phone number)</li>
              <li>Account credentials</li>
              <li>Billing and payment information</li>
              <li>Business information (company name, role)</li>
            </ul>
            <h3 className="text-lg font-medium text-zinc-800 mb-2">Usage Information</h3>
            <ul className="list-disc list-inside text-zinc-600 mb-4 space-y-1">
              <li>Log data (IP address, browser type, pages visited)</li>
              <li>Device information</li>
              <li>Usage patterns and preferences</li>
            </ul>
            <h3 className="text-lg font-medium text-zinc-800 mb-2">Third-Party Data</h3>
            <p className="text-zinc-600 leading-relaxed">
              When you connect third-party services (such as Intuit QuickBooks), we may receive data from those services in accordance with their terms and your authorization. This may include financial data, transaction records, and business information necessary to provide our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-zinc-600 leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside text-zinc-600 space-y-1">
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative information and updates</li>
              <li>Respond to inquiries and provide customer support</li>
              <li>Monitor and analyze usage trends</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-zinc-600 leading-relaxed mb-4">
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-zinc-600 space-y-1">
              <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>With Your Consent:</strong> When you have given us permission to share your information</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">5. Data Security</h2>
            <p className="text-zinc-600 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">6. Data Retention</h2>
            <p className="text-zinc-600 leading-relaxed">
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements. When data is no longer needed, we will securely delete or anonymize it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">7. Your Rights</h2>
            <p className="text-zinc-600 leading-relaxed mb-4">
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc list-inside text-zinc-600 space-y-1">
              <li>Access and receive a copy of your data</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-zinc-600 leading-relaxed mt-4">
              To exercise these rights, please contact us at <a href="mailto:contact@arbcor.com" className="text-zinc-900 underline hover:text-zinc-600">contact@arbcor.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">8. Third-Party Services</h2>
            <p className="text-zinc-600 leading-relaxed">
              Our Service may integrate with third-party services, including Intuit QuickBooks. These third-party services have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices of third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">9. Children's Privacy</h2>
            <p className="text-zinc-600 leading-relaxed">
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">10. International Data Transfers</h2>
            <p className="text-zinc-600 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. We take appropriate safeguards to ensure your data remains protected in accordance with this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-zinc-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-zinc-900 mb-4">12. Contact Us</h2>
            <p className="text-zinc-600 leading-relaxed">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <p className="text-zinc-600 mt-2">
              <a href="mailto:contact@arbcor.com" className="text-zinc-900 underline hover:text-zinc-600">contact@arbcor.com</a>
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-zinc-200 text-zinc-400 text-xs tracking-wider text-center">
          &copy; {new Date().getFullYear()} Arbcor Systems
        </div>
      </div>
    </div>
  );
};

export default Privacy;
